import Header, { type HeaderProps } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableCellNum,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  checkEnvironmentVariables,
  checkSupabaseConnection,
  diagnoseMusicTable,
  fetchLatestDate,
  fetchMusicDataDirect,
  insertSampleData,
  supabase,
  testDirectConnection,
} from '@/lib/supabase';
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  ExternalLink,
  Hash,
  ListMusic,
  Loader2,
  Music,
  RefreshCw,
  User,
  Youtube,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import '@/styles/main.scss';
import '@/styles/table.scss';
import '@/styles/tooltip.scss';
import '@/styles/skeleton.scss';
import ChartModal, { type ChartModalProps } from '@/components/ChartModal';
import ExportButton, { type ExportButtonProps } from '@/components/ExportButton';
import { filterExcludedTitles } from '@/config/excludedTitles';
import type { MusicData } from '@/types/music';
import axios from 'axios';
import { toast } from 'sonner';

// Google Identity Services 타입 정의
interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
  error?: string;
}

interface GoogleTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}

interface GoogleOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: unknown) => void;
    enable_granular_consent?: boolean;
  }) => GoogleTokenClient;
}

interface GoogleAccounts {
  oauth2: GoogleOAuth2;
}

interface GoogleIdentityServices {
  accounts: GoogleAccounts;
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const PAGE_SIZE = 10;

// 대문자로 저장된 텍스트를 적절한 대소문자로 변환하는 함수
// 통계를 위해 대문자로 저장된 경우를 대비
const normalizeCase = (text: string): string => {
  if (!text) return text;
  // 이미 적절한 대소문자가 있는 경우 (대문자만으로 구성되지 않은 경우)
  if (text !== text.toUpperCase()) {
    return text; // 원본 반환
  }
  // 전체가 대문자인 경우 Title Case로 변환 시도
  // 단, 일반적인 규칙 적용 (첫 글자만 대문자로, 나머지는 소문자)
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => {
      // 매우 짧은 단어(1-2글자)는 그대로 두거나 첫 글자만 대문자
      if (word.length <= 2) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // 일반 단어는 첫 글자만 대문자
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const SkeletonTable = () => {
  const skeletonRowKeys = Array.from(
    { length: PAGE_SIZE },
    (_, i) => `skeleton-row-${i}-${Date.now()}`
  );
  const skeletonButtonKeys = Array.from(
    { length: 5 },
    (_, i) => `skeleton-button-${i}-${Date.now()}`
  );

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <Table className="skeleton-table">
          <TableHeader className="table-header">
            <TableRow>
              <TableHead className="hover:text-primary transition-colors duration-200 cursor-pointer group">
                <div className="inline-flex items-center gap-1.5 justify-center w-full">
                  <Calendar className="h-4 w-4 group-hover:text-primary group-hover:rotate-12 transition-all duration-200" />
                  <span>DATE</span>
                </div>
              </TableHead>
              <TableHead className="number-column hover:text-primary transition-colors duration-200 cursor-pointer group">
                <div className="inline-flex items-center gap-1.5 justify-center w-full">
                  <Hash className="h-4 w-4 group-hover:text-primary group-hover:scale-110 transition-all duration-200" />
                </div>
              </TableHead>
              <TableHead className="hover:text-primary transition-colors duration-200 cursor-pointer group">
                <div className="inline-flex items-center gap-1.5 w-full">
                  <Music className="h-4 w-4 group-hover:text-primary group-hover:rotate-12 transition-all duration-200" />
                  <span>TITLE</span>
                </div>
              </TableHead>
              <TableHead className="hover:text-primary transition-colors duration-200 cursor-pointer group">
                <div className="inline-flex items-center gap-1.5 w-full">
                  <User className="h-4 w-4 group-hover:text-primary group-hover:scale-110 transition-all duration-200" />
                  <span>ARTIST</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {skeletonRowKeys.map((key) => (
              <TableRow key={key} className="skeleton-row">
                <TableCell className="h-[56px]">
                  <div className="skeleton skeleton-cell h-5" />
                </TableCell>
                <TableCell className="h-[56px]">
                  <div className="skeleton skeleton-cell h-5" />
                </TableCell>
                <TableCell className="h-[56px]">
                  <div className="skeleton skeleton-cell h-5" />
                </TableCell>
                <TableCell className="h-[56px]">
                  <div className="skeleton skeleton-cell h-5" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="skeleton-pagination">
        <div className="skeleton skeleton-info h-5 w-32" />
        <div className="flex items-center gap-2">
          {skeletonButtonKeys.map((key) => (
            <div key={key} className="skeleton skeleton-button h-9 w-9" />
          ))}
        </div>
      </div>
    </div>
  );
};

const LoadingOverlay = () => {
  return (
    <div className="loading-overlay">
      <div className="spinner-container">
        <div className="spinner" />
      </div>
    </div>
  );
};

// Helper function using fetch as provided by the user
async function createPlaylistInternal(
  accessToken: string,
  title: string,
  videoIds: string[]
): Promise<{ success: boolean; playlistId?: string; error?: string }> {
  let playlistId: string | null = null;
  try {
    // 1. 플레이리스트 생성
    const playlistRes = await fetch(
      'https://www.googleapis.com/youtube/v3/playlists?part=snippet,status',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snippet: {
            title,
            description: 'Generated by My Radio App', // Description updated
          },
          status: {
            privacyStatus: 'private', // or 'public', 'unlisted'
          },
        }),
      }
    );

    if (!playlistRes.ok) {
      const errorData = await playlistRes.json();
      console.error('Error creating playlist:', errorData);
      throw new Error(
        `Playlist creation failed: ${errorData.error?.message || playlistRes.statusText}`
      );
    }

    const playlist = await playlistRes.json();
    playlistId = playlist.id;
    console.log(`Playlist created: ID = ${playlistId}`);

    if (!playlistId) {
      throw new Error('Failed to get playlist ID after creation.');
    }

    // 2. 각 영상 추가 (실패해도 계속 진행)
    let addedCount = 0;
    let failedCount = 0;
    for (const videoId of videoIds) {
      try {
        const itemRes = await fetch(
          'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              snippet: {
                playlistId: playlistId,
                resourceId: {
                  kind: 'youtube#video',
                  videoId,
                },
              },
            }),
          }
        );

        if (!itemRes.ok) {
          const errorData = await itemRes.json();
          console.warn(`Failed to add video ${videoId}:`, errorData);
          failedCount++;
        } else {
          await itemRes.json(); // Process response if needed
          console.log(`Added video ${videoId} to playlist ${playlistId}`);
          addedCount++;
        }
      } catch (itemError) {
        console.warn(`Error adding video ${videoId}:`, itemError);
        failedCount++;
      }
    }

    console.log(`Finished adding videos. Added: ${addedCount}, Failed: ${failedCount}`);
    return { success: true, playlistId };
  } catch (error: unknown) {
    console.error('Error in createPlaylistInternal:', error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error during playlist creation/adding videos.';
    return { success: false, error: errorMessage };
  }
}

const MusicTable = () => {
  const [data, setData] = useState<MusicData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const isMounted = useRef(true);
  const [connectionStatus, setConnectionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<{
    success: boolean;
    count?: number;
    message?: string;
    error?: string;
  } | null>(null);
  const [apiTestResult, setApiTestResult] = useState<{
    success: boolean;
    message: string;
    error?: string;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSong, setSelectedSong] = useState<{
    title: string;
    artist: string;
  } | null>(null);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const isInitialMount = useRef(true);
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const googleTokenClient = useRef<GoogleTokenClient | null>(null);

  // Function to fetch and set the latest date
  const loadLatestDate = useCallback(async (): Promise<string | null> => {
    console.log('Fetching latest date with data...');
    try {
      const latestDateResult = await fetchLatestDate();

      if (isMounted.current) {
        if (latestDateResult.success && latestDateResult.latestDate) {
          console.log('Latest date fetched successfully:', latestDateResult.latestDate);
          setSelectedDate(latestDateResult.latestDate);
          localStorage.setItem('selectedDate', latestDateResult.latestDate);
          return latestDateResult.latestDate;
        }
        console.warn('Could not fetch latest date:', latestDateResult.message);
        return null;
      }
    } catch (error) {
      console.error('Error fetching latest date:', error);
      if (isMounted.current) {
        setError(
          `최신 날짜 로딩 중 오류 발생: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }
    return null;
  }, []);

  useEffect(() => {
    isMounted.current = true;
    isInitialMount.current = true;
    document.documentElement.classList.add('dark');

    const initialize = async () => {
      setError(null);
      setLoading(true);

      try {
        // Try to get date from localStorage first
        const savedDate = localStorage.getItem('selectedDate');

        if (savedDate) {
          console.log('Found saved date in local storage:', savedDate);
          if (isMounted.current) {
            setSelectedDate(savedDate);
          }
        } else {
          // If no saved date, fetch the latest date
          console.log('No saved date found. Fetching latest date...');
          await loadLatestDate();
        }
      } catch (error) {
        if (isMounted.current) {
          console.error('Error during initialization (fetching latest date):', error);
          setError(
            `초기화 중 오류 발생: ${error instanceof Error ? error.message : String(error)}`
          );
          setData([]);
          setTotalCount(0);
          setPage(0);
          setLoading(false);
        }
      }
    };

    initialize();

    const runDiagnostics = async () => {
      try {
        console.log('Running enhanced Supabase diagnostics...');

        const envCheck = checkEnvironmentVariables();
        console.log('Environment variables check:', envCheck);

        if (!envCheck.success) {
          setError(`환경 변수 문제: ${envCheck.message}`);
          return;
        }

        const directTest = await testDirectConnection();
        console.log('Direct API test result:', directTest);
        setApiTestResult({
          success: directTest.success,
          message: directTest.message,
          error:
            directTest.error instanceof Error
              ? directTest.error.message
              : typeof directTest.error === 'string'
                ? directTest.error
                : undefined,
        });

        if (!directTest.success) {
          setError(`API 연결 오류: ${directTest.message}`);
          return;
        }

        const connectionResult = await checkSupabaseConnection();
        setConnectionStatus(connectionResult);
        console.log('Connection check result:', connectionResult);

        if (!connectionResult.success) {
          setError(`데이터베이스 연결 오류: ${connectionResult.message}`);
          return;
        }

        const tableResult = await diagnoseMusicTable();
        console.log('Table diagnosis result:', tableResult);
        setDiagnosticInfo({
          success: tableResult.success,
          count: tableResult.count,
          message: tableResult.message,
          error:
            tableResult.error instanceof Error
              ? tableResult.error.message
              : typeof tableResult.error === 'string'
                ? tableResult.error
                : undefined,
        });

        if (tableResult.success && (!tableResult.count || tableResult.count === 0)) {
          console.log('No data found in table, suggesting sample data insertion');
          setError('테이블에 데이터가 없습니다. 샘플 데이터를 추가하시겠습니까?');
        } else if (tableResult.success && tableResult.count && tableResult.count > 0) {
          setError(null);
        }
      } catch (err) {
        console.error('Diagnostics failed:', err);
        setError(`진단 오류: ${err instanceof Error ? err.message : String(err)}`);
      }
    };
    runDiagnostics();

    return () => {
      isMounted.current = false;
    };
  }, [loadLatestDate]);

  const fetchData = useCallback(
    async (
      currentPage: number,
      dateFilterParam: string | null = selectedDate,
      searchTerm: string | null = null
    ) => {
      if (!isMounted.current) {
        return null;
      }

      // If no date is selected, try to load the latest date
      let dateFilter = dateFilterParam;
      if (!dateFilter) {
        if (!isInitialMount.current) {
          console.log('No date selected, attempting to load latest date');
          const latestDate = await loadLatestDate();

          // If still no date available after trying to load latest, clear the data
          if (!latestDate) {
            setData([]);
            setTotalCount(0);
            setPage(0);
            setLoading(false);
            return null;
          }

          // Use the latest date for the query
          dateFilter = latestDate;
        } else {
          return null;
        }
      }

      console.log(
        `Fetching data - Page: ${currentPage}, Date: ${dateFilter}, Search: ${searchTerm}`
      );
      setLoading(true);

      try {
        const safePageIndex = Math.max(0, currentPage);

        console.log(
          `Calling fetchMusicDataDirect with page: ${safePageIndex}, pageSize: ${PAGE_SIZE}, date: ${dateFilter}`
        );

        const result = await fetchMusicDataDirect(safePageIndex, PAGE_SIZE, dateFilter);

        console.log(`Server response for page ${safePageIndex}:`, {
          success: result.success,
          count: result.totalCount,
          dataLength: result.data?.length || 0,
          message: result.message,
        });

        if (!isMounted.current) return null;

        if (result.success) {
          // 제외할 곡 제목 필터링
          const originalData = result.data || [];
          const filteredData = filterExcludedTitles(originalData);

          // 필터링된 데이터의 넘버를 1부터 순차적으로 재정렬
          const renumberedData = filteredData.map((item, index) => ({
            ...item,
            number: index + 1,
          }));

          // 현재 페이지에서 제외된 항목 수
          const excludedInCurrentPage = originalData.length - filteredData.length;

          // totalCount 조정: 전체 개수에서 현재 페이지에서 제외된 항목 수를 뺌
          // 주의: 이는 근사치이며, 다른 페이지에도 제외될 항목이 있을 수 있음
          // 정확한 개수를 위해서는 서버 측에서 필터링하는 것이 좋지만,
          // 현재는 클라이언트 측 필터링으로 처리
          const adjustedTotalCount = Math.max(0, (result.totalCount ?? 0) - excludedInCurrentPage);

          setTotalCount(adjustedTotalCount);
          setData(renumberedData);
          setPage(safePageIndex);
          setError(null);
        } else {
          setError(result.message || '데이터를 가져오는 데 실패했습니다.');
          console.error('Fetch error:', result.error);
        }
        return result;
      } catch (error: unknown) {
        console.error('Error fetching data:', error);
        if (isMounted.current) {
          const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류';
          setError(`데이터 로딩 중 오류 발생: ${errorMessage}`);
        }
        return null;
      } finally {
        if (isMounted.current) {
          setLoading(false);
          if (isInitialMount.current) {
            isInitialMount.current = false;
          }
        }
      }
    },
    [selectedDate, loadLatestDate]
  );

  useEffect(() => {
    if (!selectedDate || !isMounted.current) {
      if (!isInitialMount.current) {
        setData([]);
        setTotalCount(0);
        setPage(0);
        setError(null);
        setLoading(false);
      }
      return;
    }

    console.log('Selected date changed to:', selectedDate, 'Fetching data...');
    fetchData(0, selectedDate).catch((error) => {
      console.error('Date change data load failed:', error);
    });
    // fetchData already handles loading state and duplicate call prevention
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleChangePage = useCallback(
    (newPage: number) => {
      if (!isMounted.current || loading || !selectedDate) return;

      const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
      const safeNewPage = Math.max(0, Math.min(newPage, totalPages - 1));

      console.log(
        `handleChangePage called with newPage: ${newPage}, safe page: ${safeNewPage}, current page: ${page}, isLoading: ${loading}, totalPages: ${totalPages}`
      );

      if (safeNewPage === page) {
        console.log(`handleChangePage(${newPage}): Bailing out (page=${page})`);
        return;
      }
      fetchData(safeNewPage, selectedDate).catch((error) => {
        console.error('Page change data load failed:', error);
      });
    },
    [loading, page, totalCount, selectedDate, fetchData]
  );

  const handleDateChange = (date: string) => {
    if (date === selectedDate) return;
    setSelectedDate(date);
    localStorage.setItem('selectedDate', date);
    setPage(0);
  };

  const getPageNumbers = useCallback(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const currentPage = page + 1;
    const pageNumbers = [];
    const maxVisiblePages = 2;

    if (totalPages <= 1) {
      return [1];
    }

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push('...');
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  }, [totalCount, page]);

  useEffect(() => {
    console.log('Pagination State:', {
      error,
      loading,
      dataLength: data.length,
      totalCount,
      rowsPerPage: PAGE_SIZE,
      page,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
      pageNumbers: getPageNumbers(),
    });
  }, [page, totalCount, data.length, error, loading, getPageNumbers]);

  const handleRetry = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      if (selectedDate) {
        await fetchData(page, selectedDate);
      } else {
        // If no date is selected, get the latest date
        console.log('No date selected, fetching latest date for retry');
        await loadLatestDate();
      }
    } catch (err) {
      console.error('Retry failed:', err);
      setError(`재시도 실패: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  }, [fetchData, page, selectedDate, loadLatestDate]);

  const handleSearchResultClick = (title: string, artist: string) => {
    setSelectedSong({ title, artist });
    setIsChartModalOpen(true);
  };

  const handleModalClose = () => {
    setIsChartModalOpen(false);
    setSelectedSong(null);
  };

  const handleExport = useCallback((date: string) => {
    console.log('Export initiated for date:', date);
  }, []);

  const searchYoutubeVideo = useCallback(async (query: string): Promise<string | null> => {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) {
      console.error('VITE_GOOGLE_API_KEY is not set.');
      setError('Google API 키가 설정되지 않았습니다.');
      return null;
    }

    try {
      const response = await axios.get('https://www.googleapis.com/youtube/v3/search', {
        params: {
          part: 'snippet',
          q: query,
          key: apiKey,
          maxResults: 1,
          type: 'video',
        },
      });

      if (response.data.items && response.data.items.length > 0) {
        return response.data.items[0].id.videoId;
      }
      console.warn(`No video found for query: ${query}`);
      return null;
    } catch (error: unknown) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error';
      console.error(`Error searching YouTube video for query "${query}":`, errorMessage);
      return null;
    }
  }, []); // setError is a stable state setter, no need to include in dependencies

  const createYoutubePlaylist = useCallback(
    async (accessToken: string) => {
      if (!selectedDate) {
        toast.warning('날짜를 먼저 선택해주세요.');
        setIsCreatingPlaylist(false);
        return;
      }
      console.log(`[createYoutubePlaylist] Starting for date: ${selectedDate}`);

      setIsCreatingPlaylist(true);
      setError(null);

      let toastId: string | number | undefined = undefined;

      try {
        toastId = toast.loading(`\'${selectedDate}\' 날짜의 전체 음악 목록 로딩 중...`);
        console.log(`[createYoutubePlaylist] Fetching all data for date: ${selectedDate}`);
        const allDataResult = await fetchMusicDataDirect(0, 9999, selectedDate);

        if (!allDataResult || !allDataResult.success || !allDataResult.data) {
          console.error(
            '[createYoutubePlaylist] Failed to fetch all data for the selected date:',
            allDataResult?.message
          );
          toast.error('선택된 날짜의 전체 음악 데이터를 가져오는데 실패했습니다.', { id: toastId });
          setError(allDataResult?.message || '데이터 로딩 실패');
          setIsCreatingPlaylist(false);
          return;
        }

        const playlistData = allDataResult.data;
        console.log(
          `[createYoutubePlaylist] Successfully fetched ${playlistData.length} items for ${selectedDate}.`
        );

        if (playlistData.length === 0) {
          console.warn(
            '[createYoutubePlaylist] No data found for the selected date after fetching all.'
          );
          toast.warning('선택된 날짜에 음악 데이터가 없습니다.', { id: toastId });
          setIsCreatingPlaylist(false);
          return;
        }

        const playlistName = `My Radio ${selectedDate}`;

        toast.loading(`[1/${playlistData.length}] YouTube 영상 검색 중...`, { id: toastId });
        console.log(
          `[createYoutubePlaylist] Starting YouTube search for ${playlistData.length} items.`
        );

        const searchPromises = playlistData.map(async (item, index) => {
          const query = `${item.artist} ${item.title}`;
          const videoId = await searchYoutubeVideo(query);
          toast.loading(`[${index + 1}/${playlistData.length}] YouTube 영상 검색 중...`, {
            id: toastId,
          });
          if (!videoId) {
            console.warn(`[createYoutubePlaylist] Video not found for: ${query}`);
          }
          return videoId;
        });

        const videoIdResults = await Promise.all(searchPromises);
        const validVideoIds = videoIdResults.filter((id): id is string => id !== null);

        const foundCount = validVideoIds.length;
        const notFoundCount = playlistData.length - foundCount;
        console.log(
          `[createYoutubePlaylist] YouTube search complete. Found: ${foundCount}, Not Found: ${notFoundCount}`
        );

        if (validVideoIds.length === 0) {
          const errorMsg = `YouTube 영상을 찾지 못했습니다. 플레이리스트를 생성할 수 없습니다.${notFoundCount > 0 ? ` (${notFoundCount}개 검색 실패)` : ''}`;
          setError(errorMsg);
          toast.error(errorMsg, { id: toastId });
          setIsCreatingPlaylist(false);
          return;
        }

        toast.loading(`'${playlistName}' 플레이리스트 생성 및 ${foundCount}곡 추가 시작...`, {
          id: toastId,
        });
        console.log(
          `[createYoutubePlaylist] Calling createPlaylistInternal with ${validVideoIds.length} video IDs.`
        );

        const result = await createPlaylistInternal(accessToken, playlistName, validVideoIds);

        if (result.success && result.playlistId) {
          console.log(
            `[createYoutubePlaylist] Playlist creation successful. ID: ${result.playlistId}`
          );
          const successMessage = `총 ${foundCount}개 영상 검색됨.${notFoundCount > 0 ? ` (${notFoundCount}곡 검색 실패)` : ''}`;
          const playlistUrl = `https://www.youtube.com/playlist?list=${result.playlistId}`;

          toast.success(`플레이리스트 '${playlistName}' 생성 완료!`, {
            id: toastId,
            description: successMessage,
            duration: 10000,
            action: {
              label: '플레이리스트 보기',
              onClick: () => window.open(playlistUrl, '_blank'),
            },
          });
        } else {
          console.error('[createYoutubePlaylist] Playlist creation failed:', result.error);
          const errorMessage = result.error || '알 수 없는 오류 발생';
          setError(`YouTube 플레이리스트 생성 중 오류 발생: ${errorMessage}`);
          toast.error('플레이리스트 생성 실패', {
            id: toastId,
            description: errorMessage,
            duration: 10000,
          });
        }
      } catch (error) {
        console.error('[createYoutubePlaylist] Unexpected error:', error);
        const errorMsg = error instanceof Error ? error.message : '예상치 못한 오류 발생';
        setError(`플레이리스트 생성 중 예상치 못한 오류: ${errorMsg}`);
        if (toastId) {
          toast.error('플레이리스트 생성 중 예상치 못한 오류', {
            id: toastId,
            description: errorMsg,
          });
        } else {
          toast.error(`플레이리스트 생성 중 예상치 못한 오류: ${errorMsg}`);
        }
      } finally {
        setIsCreatingPlaylist(false);
      }
    },
    [selectedDate, searchYoutubeVideo]
  ); // setError, setIsCreatingPlaylist are stable state setters; fetchMusicDataDirect is imported function; data is not used

  const handleCreatePlaylist = useCallback(async () => {
    if (!selectedDate) {
      toast.warning('날짜를 먼저 선택해주세요.');
      return;
    }
    setIsCreatingPlaylist(true);

    // Initialize Google API if not already initialized
    if (!googleTokenClient.current) {
      console.log('Initializing Google API client...');

      try {
        // Load Google API script if not already loaded
        if (!window.google) {
          console.log('Google API not loaded yet, loading script...');

          // Check if script is already in the process of loading
          if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
            console.log('Google API script tag already exists, waiting for it to load...');
            // Check again in 1 second
            setTimeout(() => {
              if (window.google?.accounts?.oauth2) {
                console.log('Google API loaded after waiting');
                initializeGoogleAuth();
              } else {
                console.error('Google API still not loaded after waiting');
                toast.error('Google API 스크립트 로드 타임아웃');
                setError('Google API 스크립트가 로드되지 않았습니다. 인터넷 연결을 확인하세요.');
                setIsCreatingPlaylist(false);
              }
            }, 1000);
            return;
          }

          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;

          // Add load event listener before appending to DOM
          script.onload = () => {
            console.log('Google API script loaded successfully');
            // Verify the API is actually available
            if (window.google?.accounts?.oauth2) {
              console.log('Google OAuth2 API verified as available');
              initializeGoogleAuth();
            } else {
              console.error('Google API script loaded but API objects not available');
              toast.error('Google API가 올바르게 로드되지 않았습니다');
              setError(
                'Google API 객체를 찾을 수 없습니다. 브라우저 캐시를 지우고 다시 시도해보세요.'
              );
              setIsCreatingPlaylist(false);
            }
          };

          script.onerror = (event) => {
            console.error('Failed to load Google API script', event);
            toast.error('Google API 스크립트 로드에 실패했습니다.');
            setError('Google API 스크립트 로드에 실패했습니다. 인터넷 연결을 확인하세요.');
            setIsCreatingPlaylist(false);
          };

          document.body.appendChild(script);
        } else {
          console.log('Google API already available in window object');
          initializeGoogleAuth();
        }
      } catch (error) {
        console.error('Error loading Google API:', error);
        toast.error('Google API 초기화 중 오류가 발생했습니다.');
        setError(
          `Google API 초기화 오류: ${error instanceof Error ? error.message : String(error)}`
        );
        setIsCreatingPlaylist(false);
      }

      // Function to initialize Google Auth after script is loaded
      function initializeGoogleAuth() {
        try {
          console.log('Initializing Google OAuth...');
          console.log('VITE_GOOGLE_CLIENT_ID exists:', !!import.meta.env.VITE_GOOGLE_CLIENT_ID);
          console.log(
            'VITE_GOOGLE_CLIENT_ID length:',
            import.meta.env.VITE_GOOGLE_CLIENT_ID?.length || 0
          );
          console.log('window.google exists:', !!window.google);
          console.log('window.google.accounts exists:', !!window.google?.accounts);
          console.log('window.google.accounts.oauth2 exists:', !!window.google?.accounts?.oauth2);

          if (window.google?.accounts?.oauth2) {
            console.log(
              'Available methods on window.google.accounts.oauth2:',
              Object.keys(window.google.accounts.oauth2)
            );

            // Create token client
            if (!window.google?.accounts?.oauth2) {
              throw new Error('Google OAuth2 API not available');
            }
            googleTokenClient.current = window.google.accounts.oauth2.initTokenClient({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              scope: 'https://www.googleapis.com/auth/youtube.force-ssl',
              callback: (tokenResponse: GoogleTokenResponse) => {
                if (tokenResponse?.access_token) {
                  console.log('Google OAuth token received successfully');
                  setGoogleAccessToken(tokenResponse.access_token);

                  // Now that we have the token, create the playlist
                  createYoutubePlaylist(tokenResponse.access_token);
                } else {
                  console.error('Token response received but no access_token:', tokenResponse);
                  toast.error('인증은 성공했으나 액세스 토큰을 받지 못했습니다');
                  setError('인증은 성공했으나 액세스 토큰을 받지 못했습니다');
                  setIsCreatingPlaylist(false);
                }
              },
              error_callback: (error: unknown) => {
                console.error('Google OAuth error:', error);
                console.error('Google OAuth error details:', JSON.stringify(error));

                // Handle unverified app warning - provide helpful guidance
                const errorObj =
                  error && typeof error === 'object' && 'type' in error
                    ? (error as { type?: string })
                    : null;

                if (errorObj?.type === 'popup_closed' || errorObj?.type === 'popup_blocked') {
                  toast.error('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
                  setError('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
                } else if (errorObj?.type === 'access_denied') {
                  toast.error(
                    '인증이 취소되었습니다. 플레이리스트 생성을 위해 Google 인증이 필요합니다.'
                  );
                  setError('인증이 취소되었습니다.');
                } else {
                  toast.error(`Google 인증 오류: ${errorObj?.type || '알 수 없는 오류'}`);
                  setError(`Google 인증 오류: ${errorObj?.type || '알 수 없는 오류'}`);
                }
                setIsCreatingPlaylist(false);
              },
              // Enable granular consent for better user experience
              enable_granular_consent: true,
            });

            console.log('Google API client initialized successfully');
            // After initialization, request the token
            // Note: If you see "unverified app" warning, click "Advanced" then "Go to [App Name] (unsafe)"
            // This is normal for apps in development/testing mode
            googleTokenClient.current.requestAccessToken({
              prompt: 'consent',
            });
          } else {
            console.error('Google API client library not loaded or OAuth2 module missing');
            toast.error('Google API 클라이언트 라이브러리가 로드되지 않았습니다.');
            setError('Google API 클라이언트 라이브러리가 로드되지 않았습니다.');
            setIsCreatingPlaylist(false);
          }
        } catch (error) {
          console.error('Error initializing Google API client:', error);
          if (error instanceof Error) {
            console.error('Error name:', error.name);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
          }
          toast.error('Google 인증 클라이언트 초기화 중 오류가 발생했습니다.');
          setError(
            `Google API 클라이언트 초기화 오류: ${error instanceof Error ? error.message : String(error)}`
          );
          setIsCreatingPlaylist(false);
        }
      }
    } else if (googleAccessToken) {
      console.log('Using existing Google Access Token for playlist creation.');
      await createYoutubePlaylist(googleAccessToken);
    } else {
      console.log('Requesting Google Access Token for playlist creation...');
      try {
        googleTokenClient.current.requestAccessToken({ prompt: 'consent' });
      } catch (requestError) {
        console.error('Error requesting Google Access Token:', requestError);
        toast.error('Google 인증 토큰 요청 중 오류가 발생했습니다.');
        setError('Google 인증 토큰 요청 중 오류 발생.');
        setIsCreatingPlaylist(false);
      }
    }
  }, [selectedDate, googleAccessToken, createYoutubePlaylist]);

  const handleRefresh = () => {
    if (!selectedDate) {
      handleRetry();
      return;
    }
    setLoading(true);
    setError(null);
    fetchData(page, selectedDate).catch((err) => console.error('Refresh failed', err));
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Format date for display (YYYY-MM-DD -> YYYY년 MM월 DD일)
  const formatDateDisplay = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const [year, month, day] = dateString.split('-');
      if (year && month && day) {
        return `${year}년 ${Number.parseInt(month, 10)}월 ${Number.parseInt(day, 10)}일`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const getPaginationRange = useCallback(() => {
    return [1];
  }, []);

  const [sortColumn, setSortColumn] = useState<keyof MusicData | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | ''>('');

  const handleSort = (column: keyof MusicData) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    } else if (sortColumn === column && sortDirection === 'desc') {
      setSortColumn('');
      setSortDirection('');
      handleRefresh();
      return;
    }
    setSortColumn(column);
    setSortDirection(direction);
    console.log(`Sorting by ${column} ${direction}`);
    handleRefresh();
  };

  const renderSortIndicator = (column: keyof MusicData) => {
    if (sortColumn !== column || sortDirection === '') return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <TooltipProvider>
      <div className="container">
        <Header onDateChange={handleDateChange} onSearchResultClick={handleSearchResultClick} />

        <div className="text-center my-6">
          <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2 text-white">
            <ListMusic className="h-7 w-7 text-primary" />
            Daily Setlist
            {selectedDate && (
              <span className="text-xl text-blue-400 font-normal ml-2">
                ({formatDateDisplay(selectedDate)})
              </span>
            )}
          </h1>
          <p className="text-gray-400">Browse the daily music setlist</p>
        </div>

        {loading && <SkeletonTable />}
        {loading && !isInitialMount.current && <LoadingOverlay />}
        {!loading && !isInitialMount.current && data.length === 0 && !error && selectedDate && (
          <div className="message-container empty-data flex flex-col items-center justify-center text-center p-8 border rounded-lg shadow-sm bg-card text-card-foreground min-h-[200px] my-8 mx-auto max-w-md">
            <ListMusic className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-1">
              선택하신 날짜 '{selectedDate}'에 데이터가 없습니다.
            </p>
            <p className="text-sm text-gray-400">다른 날짜를 선택해주세요.</p>
          </div>
        )}
        {!loading && !isInitialMount.current && data.length === 0 && !error && !selectedDate && (
          <div className="message-container empty-data flex flex-col items-center justify-center text-center p-8 border rounded-lg shadow-sm bg-card text-card-foreground min-h-[200px] my-8 mx-auto max-w-md">
            <Calendar className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium">날짜를 선택하여 음악 목록을 확인하세요.</p>
            <p className="text-sm text-gray-400 mt-1">
              또는 데이터가 있는 최근 날짜가 자동으로 로드됩니다.
            </p>
          </div>
        )}
        {error && (
          <div className="message-container error-message flex flex-col items-center justify-center text-center p-8 border border-destructive rounded-lg shadow-sm bg-destructive/10 text-destructive min-h-[200px] my-8 mx-auto max-w-md">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-semibold mb-1">오류 발생</p>
            <p className="text-destructive">{error}</p>
            <div className="mt-4 space-x-2">
              <Button onClick={handleRetry} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" /> 다시 시도
              </Button>
            </div>
          </div>
        )}
        {!loading && data.length > 0 && selectedDate && (
          <div className="table-container">
            <div className="table-wrapper">
              <Table>
                <TableHeader className="table-header">
                  <TableRow>
                    <TableHead
                      onClick={() => handleSort('broadcast_date')}
                      className="hidden md:table-cell cursor-pointer"
                    >
                      DATE {renderSortIndicator('broadcast_date')}
                    </TableHead>
                    <TableHead onClick={() => handleSort('number')} className="cursor-pointer">
                      # {renderSortIndicator('number')}
                    </TableHead>
                    <TableHead onClick={() => handleSort('title')} className="cursor-pointer">
                      TITLE {renderSortIndicator('title')}
                    </TableHead>
                    <TableHead onClick={() => handleSort('artist')} className="cursor-pointer">
                      ARTIST {renderSortIndicator('artist')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((item) => (
                    <TableRow key={item.seqid} className="table-row">
                      <TableCell className="hidden text-center md:table-cell">
                        {item.broadcast_date}
                      </TableCell>
                      <TableCellNum className="text-center">{item.number}</TableCellNum>
                      <TableCell className="truncate max-w-[300px] md:max-w-none">
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(
                                    `https://www.youtube.com/results?search_query=${encodeURIComponent(
                                      `${item.artist} ${item.title}`
                                    )}`,
                                    '_blank'
                                  );
                                }}
                                className="text-gray-400 hover:text-primary flex-shrink-0 h-6 w-6 p-0"
                              >
                                <Youtube className="h-4 w-4 text-[#FF0000]" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>YouTube에서 검색</p>
                            </TooltipContent>
                          </Tooltip>
                          <span className="truncate">{normalizeCase(item.title)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="truncate max-w-[200px] md:max-w-none">
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSearchResultClick(item.title, item.artist)}
                                className="text-gray-400 hover:text-primary h-6 w-6 p-0 flex-shrink-0"
                              >
                                <Database className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              <p>통계 보기</p>
                            </TooltipContent>
                          </Tooltip>
                          <span className="truncate">{normalizeCase(item.artist)}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="table-controls flex justify-center items-center gap-2 my-4">
              <ExportButton
                selectedDate={selectedDate}
                onExport={handleExport}
                disabled={!selectedDate}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleCreatePlaylist}
                    variant="outline"
                    size="sm"
                    disabled={isCreatingPlaylist || loading || !selectedDate || data.length === 0}
                    className="relative transition-colors duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105"
                  >
                    {isCreatingPlaylist ? (
                      <Loader2 className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 animate-spin" />
                    ) : (
                      <Youtube className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 text-[#FF0000]" />
                    )}
                    <span className="hidden md:inline">Create Playlist</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>선택된 날짜의 플레이리스트 생성 (YouTube)</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="pagination-controls flex flex-col items-center gap-2 mt-4">
              <span className="text-xs text-gray-400 font-en">
                Page {page + 1} of {totalPages} / {totalCount} items
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(page - 1)}
                  disabled={loading || page <= 0}
                  className="mr-2 font-en transition-colors duration-150 ease-in-out text-white border-gray-600 hover:bg-gray-800 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5 md:h-4 md:w-4 md:mr-1" />
                </Button>
                <div className="page-numbers flex items-center gap-1">
                  {getPageNumbers().map((pageNumber) =>
                    pageNumber === '...' ? (
                      <span key={`ellipsis-${pageNumber}`} className="px-2 font-en text-gray-400">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={pageNumber}
                        variant={page + 1 === pageNumber ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          const nextPage = Number(pageNumber) - 1;
                          handleChangePage(nextPage);
                        }}
                        className={`transition-colors duration-150 ease-in-out font-en ${
                          page + 1 === pageNumber
                            ? 'bg-blue-500 text-white border-blue-400 hover:bg-blue-600 font-bold shadow-md'
                            : 'text-gray-400 border-gray-700 hover:bg-gray-800 hover:text-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        disabled={loading || page + 1 === pageNumber}
                      >
                        {pageNumber}
                      </Button>
                    )
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(page + 1)}
                  disabled={loading || page >= Math.ceil(totalCount / PAGE_SIZE) - 1}
                  className="ml-2 font-en transition-colors duration-150 ease-in-out text-white border-gray-600 hover:bg-gray-800 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5 md:h-4 md:w-4 md:ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {selectedSong && (
          <ChartModal
            isOpen={isChartModalOpen}
            onClose={handleModalClose}
            title={selectedSong.title}
            artist={selectedSong.artist}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default MusicTable;
